import { useCallback } from 'react';
import _get from 'lodash/get';
import {
  Intervention,
  InterventionFilterInputObject,
  InterventionScopeNameEnum,
  InterventionsQueryVariables,
  useInterventionsIdsLazyQuery,
  useInterventionsQuery,
} from 'graphql-common';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { ListQueryHookProps, ListQueryResultWithIds } from '../types';

// InterventionFilterInputObject has no `id`-IN filter, so unlike
// Assets/SiteAreas/Documents this hook can't fetch an arbitrary Selected-tab
// id set. Callers needing the Selected tab must filter by a backward link
// instead (e.g. `departmentsId`, mirroring FormTemplate's `documentationsId`)
// via `additionalFilters`, or fall back to the All-tab cache.
type UseInterventionsMultiselectQueryHookProps = Omit<
  ListQueryHookProps,
  'ids' | 'scopeName'
> & {
  scopeName?: InterventionScopeNameEnum;
};

const DEFAULT_SEARCH_SCHEMA = [{ fieldName: 'name' }, { fieldName: 'code' }];

export default function useInterventionsMultiselectQueryHook({
  additionalFilters,
  fetchPolicy = 'cache-and-network',
  orderingParams,
  paginationParams,
  scopeName = InterventionScopeNameEnum.Opened,
  searchQuery,
  searchQueryFilterSchema,
  skip,
  usePrevious = true,
}: UseInterventionsMultiselectQueryHookProps): ListQueryResultWithIds<Intervention> {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(orderingParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    orderingParams,
    GetUrlParams.OrderingDirection,
  );

  const queryVariables: InterventionsQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
    scopeName,
  };

  const filters: InterventionFilterInputObject[] = [];

  if (searchQuery) {
    const schema =
      searchQueryFilterSchema && searchQueryFilterSchema.length
        ? searchQueryFilterSchema.map((fieldName) => ({ fieldName }))
        : DEFAULT_SEARCH_SCHEMA;
    filters.push(...createSearchFilters(searchQuery, schema));
  }

  if (additionalFilters?.length) {
    filters.push(...(additionalFilters as InterventionFilterInputObject[]));
  }

  if (filters.length) {
    queryVariables.filters = filters;
  }

  if (orderingField && orderingDirection) {
    queryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }

  const { loading, data, previousData, refetch, error } = useInterventionsQuery(
    {
      fetchPolicy,
      variables: queryVariables,
      skip,
    },
  );

  const [fetchIdsQuery] = useInterventionsIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const fetchIds = useCallback(
    () =>
      fetchIdsQuery({
        variables: {
          scopeName: queryVariables.scopeName,
          onlyIds: true,
          filters: queryVariables.filters,
        },
      }),
    [fetchIdsQuery, queryVariables.scopeName, queryVariables.filters],
  );

  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const collection =
    !usePrevious && loading
      ? []
      : (_get(queryData, 'data.collection', []) as Intervention[]);
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);

  return {
    fetchIds,
    refetch,
    collection,
    error,
    firstLoading,
    loading,
    totalPages,
    totalCount,
  };
}
